import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get("/public/gallery");
    setItems(r.data || []);
  };

  const addByUrl = async () => {
    if (!url) return;
    try {
      await api.post("/admin/gallery", { url, caption });
      toast.success("Added");
      setUrl(""); setCaption("");
      load();
    } catch { toast.error("Failed"); }
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/gallery", { url: `${API.replace(/\/api$/, "")}${data.url}`, caption });
      toast.success("Uploaded");
      setCaption("");
      load();
    } catch { toast.error("Upload failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete photo?")) return;
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <DashboardLayout>
      <h1 className="heading text-2xl font-bold">Gallery</h1>
      <p className="text-sm text-slate-500 mt-1">Add or remove photos shown on the public gallery.</p>
      <div className="mt-5 p-5 rounded-xl border border-slate-200 bg-white">
        <div className="grid md:grid-cols-3 gap-3">
          <input placeholder="Image URL" value={url} onChange={(e) => setUrl(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="gallery-url-input" />
          <input placeholder="Caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="gallery-caption-input" />
          <div className="flex gap-2">
            <button onClick={addByUrl} className="flex-1 bg-brand text-white rounded-lg px-3 py-2 text-sm font-semibold inline-flex items-center justify-center gap-1" data-testid="gallery-add-url-button">
              <Plus size={14} /> Add URL
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
            <button onClick={() => fileRef.current?.click()} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold inline-flex items-center justify-center gap-1" data-testid="gallery-upload-button">
              <Upload size={14} /> Upload
            </button>
          </div>
        </div>
      </div>
      <div className="mt-6 masonry">
        {items.map((it) => (
          <div key={it.id} className="rounded-xl overflow-hidden border border-slate-200 relative group" data-testid={`admin-gallery-${it.id}`}>
            <img src={it.url} alt={it.caption || "Photo"} className="w-full h-auto" />
            <button onClick={() => del(it.id)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition" data-testid={`delete-gallery-${it.id}`}>
              <Trash2 size={14} />
            </button>
            {it.caption && <div className="p-2 text-xs text-slate-600 bg-white">{it.caption}</div>}
          </div>
        ))}
        {items.length === 0 && <div className="p-10 rounded-xl border border-dashed border-slate-300 text-center text-slate-500">No photos yet.</div>}
      </div>
    </DashboardLayout>
  );
}
